import type {
  CatalogPricingProjection,
  CatalogPromotion,
  MenuItemAvailabilityRuleV2,
  MenuItemChannelOverrideV2,
  MenuItemComponentV2,
  MenuItemTagV2,
  MenuItemV2,
  ModifierGroupItemV2,
  ModifierGroupV2,
} from "@/models/menu-item-v2";
import type { MenuItemInterface } from "@/models/menu-item";

/**
 * Loose shape of one catalog snapshot row from
 * GET /api/public/v1/storefront/menu?orderChannel={channel_id}
 * (legacy catalog snapshot path retired for storefront traffic)
 * (fields may be missing or loosely typed in transport).
 */
export interface ApiMenuCatalogItemRaw {
  menu_item_id?: string;
  restaurant_id?: string;
  item_name?: string;
  description?: string;
  image_url?: string;
  menu_label?: string;
  menu_version?: number;
  base_price?: number;
  effective_price?: number | null;
  price_source?: string;
  pricing_projection?: unknown;
  promotion?: unknown;
  is_available?: boolean;
  is_deleted?: boolean;
  updated_date?: string;
  created_by?: string;
  updated_by?: string;
  tags?: unknown[];
  availability_rules?: unknown[];
  components?: unknown[];
  channel_overrides?: unknown[];
  modifier_groups?: unknown[];
}

/** {@link MenuItemV2} plus derived {@link MenuItemV2WithCategory.category}. */
export type MenuItemV2WithCategory = MenuItemV2 & { category: string };

function num(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function str(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function bool(value: unknown, fallback = false): boolean {
  if (value === null || value === undefined) return fallback;
  return Boolean(value);
}

function fallbackId(prefix: string, ...parts: Array<string | number | undefined>): string {
  const suffix = parts
    .map((p) => String(p ?? "na"))
    .join("-")
    .replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${prefix}-${suffix}`;
}

function mapTag(raw: unknown): MenuItemTagV2 | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const tag_id = str(o.tag_id);
  if (!tag_id) return null;
  return {
    tag_id,
    tag_code: str(o.tag_code),
    tag_name: str(o.tag_name),
    tag_type: str(o.tag_type),
    is_filterable: o.is_filterable as MenuItemTagV2["is_filterable"],
    is_active: o.is_active as MenuItemTagV2["is_active"],
    color_code: str(o.color_code),
    icon_url: str(o.icon_url),
    display_order: num(o.display_order, 0),
  };
}

function mapAvailabilityRule(raw: unknown): MenuItemAvailabilityRuleV2 | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const menu_item_id = str(o.menu_item_id);
  if (!menu_item_id) return null;
  return {
    menu_item_id,
    day_of_week: num(o.day_of_week, 0),
    start_time: str(o.start_time),
    end_time: str(o.end_time),
  };
}

function mapComponent(raw: unknown): MenuItemComponentV2 | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const parent = str(o.parent_menu_item_id);
  const child = str(o.child_menu_item_id);
  if (!parent || !child) return null;
  return {
    parent_menu_item_id: parent,
    child_menu_item_id: child,
    quantity: num(o.quantity, 0),
    display_order: num(o.display_order, 0),
  };
}

function mapChannelOverride(raw: unknown): MenuItemChannelOverrideV2 | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const menu_item_id = str(o.menu_item_id);
  if (!menu_item_id) return null;
  return {
    menu_item_id,
    channel_id: num(o.channel_id, 0),
    channel_price: num(o.channel_price, 0),
    override_name: str(o.override_name),
    is_active: bool(o.is_active, false),
  };
}

function mapModifierGroupItem(
  raw: unknown,
  groupIdFallback: string,
  groupIndex: number,
  itemIndex: number
): ModifierGroupItemV2 {
  if (!raw || typeof raw !== "object") {
    return {
      modifier_group_item_id: fallbackId("mod-item", groupIndex, itemIndex),
      modifier_group_id: groupIdFallback,
      child_menu_item_id: fallbackId("menu-child", groupIndex, itemIndex),
      child_menu_item_name: "",
      delta_price: 0,
      is_default: false,
      is_available: true,
      is_effectively_selectable: true,
      display_order: itemIndex,
    };
  }
  const o = raw as Record<string, unknown>;
  const modifier_group_item_id = str(o.modifier_group_item_id);
  const modifier_group_id = str(o.modifier_group_id);
  const child_menu_item_id = str(o.child_menu_item_id);
  return {
    modifier_group_item_id:
      modifier_group_item_id || fallbackId("mod-item", groupIndex, itemIndex),
    modifier_group_id: modifier_group_id || groupIdFallback,
    child_menu_item_id: child_menu_item_id || fallbackId("menu-child", groupIndex, itemIndex),
    child_menu_item_name: str(o.child_menu_item_name),
    delta_price: num(o.delta_price, 0),
    is_default: bool(o.is_default, false),
    is_available: bool(o.is_available, true),
    is_effectively_selectable: bool(o.is_effectively_selectable, true),
    display_order: num(o.display_order, 0),
  };
}

function mapModifierGroup(
  raw: unknown,
  parentMenuItemId: string,
  groupIndex: number
): ModifierGroupV2 {
  if (!raw || typeof raw !== "object") {
    return {
      modifier_group_id: fallbackId("mod-group", groupIndex),
      menu_item_id: parentMenuItemId || fallbackId("menu-parent", groupIndex),
      group_name: "",
      display_name: "Customization",
      min_select: 0,
      max_select: 0,
      display_order: groupIndex,
      items: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const modifier_group_id = str(o.modifier_group_id);
  const menu_item_id = str(o.menu_item_id);
  const resolvedGroupId = modifier_group_id || fallbackId("mod-group", groupIndex);
  const resolvedMenuItemId = menu_item_id || parentMenuItemId || fallbackId("menu-parent", groupIndex);

  const rawItems = Array.isArray(o.items) ? o.items : [];
  const items: ModifierGroupItemV2[] = rawItems
    .map((item, itemIndex) =>
      mapModifierGroupItem(item, resolvedGroupId, groupIndex, itemIndex)
    )
    .sort((a, b) => a.display_order - b.display_order);

  return {
    modifier_group_id: resolvedGroupId,
    menu_item_id: resolvedMenuItemId,
    group_name: str(o.group_name),
    display_name: str(o.display_name),
    min_select: num(o.min_select, 0),
    max_select: num(o.max_select, 0),
    display_order: num(o.display_order, 0),
    items,
  };
}

/**
 * MEALTYPE tags (fallback CATEGORY): trim, dedupe by lowercase key, stable by `display_order`.
 */
function deriveCategoryFromTags(tags: MenuItemTagV2[]): { display: string; hadTag: boolean } {
  const mealType = tags
    .filter((t) => String(t.tag_type).toUpperCase() === "MEALTYPE")
    .sort((a, b) => a.display_order - b.display_order);
  const categoryTags = (mealType.length > 0 ? mealType : tags.filter((t) => String(t.tag_type).toUpperCase() === "CATEGORY"))
    .sort((a, b) => a.display_order - b.display_order);
  const seenKeys = new Set<string>();
  const displayParts: string[] = [];
  for (const t of categoryTags) {
    const raw = str(t.tag_name).trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    displayParts.push(raw);
  }
  if (displayParts.length === 0) {
    return { display: "", hadTag: false };
  }
  return { display: displayParts.join(", "), hadTag: true };
}

/** True if a usable MEALTYPE/CATEGORY tag exists (for runtime validation in hook). */
export function v2ItemHasCategoryTag(tags: MenuItemTagV2[]): boolean {
  return tags.some(
    (t) =>
      (String(t.tag_type).toUpperCase() === "MEALTYPE" ||
        String(t.tag_type).toUpperCase() === "CATEGORY") &&
      str(t.tag_name).trim() !== ""
  );
}

function tryParsePrice(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

/**
 * Pass-through map of backend `pricing_projection`.
 * Nullability only — never invents or recalculates prices.
 */
export function mapPricingProjection(raw: unknown): CatalogPricingProjection | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const list_price = tryParsePrice(o.list_price);
  const channel_price = tryParsePrice(o.channel_price);
  const selling_price = tryParsePrice(o.selling_price);
  // Without a parseable selling_price the projection cannot serve as unit authority.
  if (list_price === null || channel_price === null || selling_price === null) {
    return null;
  }

  const basisRaw = o.selling_price_basis;
  let selling_price_basis: number | string = 0;
  if (basisRaw !== null && basisRaw !== undefined && basisRaw !== "") {
    if (typeof basisRaw === "number" && Number.isFinite(basisRaw)) {
      selling_price_basis = basisRaw;
    } else if (typeof basisRaw === "string") {
      const asNum = Number(basisRaw.trim());
      selling_price_basis =
        Number.isFinite(asNum) && basisRaw.trim() !== "" ? asNum : basisRaw;
    } else {
      const asNum = Number(basisRaw);
      selling_price_basis = Number.isFinite(asNum) ? asNum : 0;
    }
  }

  return {
    list_price,
    channel_price,
    selling_price,
    selling_price_basis,
  };
}

/**
 * Pass-through map of sibling `promotion`.
 * Empty / incomplete objects normalize to null. Never invents badge_text.
 */
export function mapCatalogPromotion(raw: unknown): CatalogPromotion | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const offer_id = str(o.offer_id).trim();
  const offer_name = str(o.offer_name).trim();
  if (!offer_id && !offer_name) return null;

  const badgeRaw = o.badge_text;
  const badge_text =
    badgeRaw === null || badgeRaw === undefined
      ? null
      : str(badgeRaw).trim() === ""
        ? null
        : str(badgeRaw);

  return {
    offer_id,
    offer_name,
    badge_text,
  };
}

/**
 * Channel override → effective → base. Never coerces “missing” to 0 without logging at error level
 * (returns 0 with `source: "error"` only when no candidate is parseable at all).
 */
export function resolveLegacyItemPrice(
  m: MenuItemV2,
  orderChannel: number
): { price: number; source: "channel" | "effective" | "base" | "error" } {
  const id = str(m.menu_item_id) || "unknown";
  const overrides = Array.isArray(m.channel_overrides) ? m.channel_overrides : [];
  for (const o of overrides) {
    if (o.is_active && o.channel_id === orderChannel) {
      const p = tryParsePrice(o.channel_price);
      if (p !== null) {
        return { price: p, source: "channel" };
      }
    }
  }

  const eff = tryParsePrice(m.effective_price);
  if (eff !== null) {
    return { price: eff, source: "effective" };
  }
  const base = tryParsePrice(m.base_price);
  if (base !== null) {
    return { price: base, source: "base" };
  }
  console.error("[menuV2Adapter] no valid price for item", { menu_item_id: id });
  return { price: 0, source: "error" };
}

/**
 * Compatibility alias: projection.selling_price when projection exists; else legacy resolver.
 * Does not invent selling prices — only selects the approved alias source.
 */
export function resolveCompatibilityItemPrice(
  m: MenuItemV2,
  orderChannel: number
): number {
  if (m.pricing_projection != null) {
    return m.pricing_projection.selling_price;
  }
  return resolveLegacyItemPrice(m, orderChannel).price;
}

/**
 * Maps one raw catalog API row into {@link MenuItemV2} plus derived `category`
 * from tags where `tag_type === "CATEGORY"`.
 *
 * Price: {@link MenuItemV2.effective_price} is set to API `effective_price` when
 * present and finite; otherwise falls back to `base_price`.
 */
export function mapApiMenuToMenuItemV2(apiResponse: ApiMenuCatalogItemRaw): MenuItemV2WithCategory {
  const tags = (Array.isArray(apiResponse.tags) ? apiResponse.tags : [])
    .map(mapTag)
    .filter((t): t is MenuItemTagV2 => t !== null)
    .sort((a, b) => a.display_order - b.display_order);

  const base_price = num(apiResponse.base_price, 0);
  const effRaw = apiResponse.effective_price;
  const effective_price =
    effRaw !== null && effRaw !== undefined && Number.isFinite(Number(effRaw))
      ? Number(effRaw)
      : base_price;

  const availability_rules = (Array.isArray(apiResponse.availability_rules)
    ? apiResponse.availability_rules
    : []
  )
    .map(mapAvailabilityRule)
    .filter((x): x is MenuItemAvailabilityRuleV2 => x !== null);

  const components = (Array.isArray(apiResponse.components) ? apiResponse.components : [])
    .map(mapComponent)
    .filter((x): x is MenuItemComponentV2 => x !== null)
    .sort((a, b) => a.display_order - b.display_order);

  const channel_overrides = (Array.isArray(apiResponse.channel_overrides)
    ? apiResponse.channel_overrides
    : []
  )
    .map(mapChannelOverride)
    .filter((x): x is MenuItemChannelOverrideV2 => x !== null);

  const modifier_groups = (Array.isArray(apiResponse.modifier_groups)
    ? apiResponse.modifier_groups
    : []
  )
    .map((group, groupIndex) =>
      mapModifierGroup(group, str(apiResponse.menu_item_id), groupIndex)
    )
    .sort((a, b) => a.display_order - b.display_order);

  const core: MenuItemV2 = {
    menu_item_id: str(apiResponse.menu_item_id),
    restaurant_id: str(apiResponse.restaurant_id),
    item_name: str(apiResponse.item_name),
    description: str(apiResponse.description),
    image_url: str(apiResponse.image_url),
    menu_label: str(apiResponse.menu_label),
    menu_version: num(apiResponse.menu_version, 0),
    base_price,
    effective_price,
    price_source: str(apiResponse.price_source),
    pricing_projection: mapPricingProjection(apiResponse.pricing_projection),
    promotion: mapCatalogPromotion(apiResponse.promotion),
    is_available: bool(apiResponse.is_available, true),
    is_deleted: bool(apiResponse.is_deleted, false),
    updated_date: str(apiResponse.updated_date),
    created_by: str(apiResponse.created_by),
    updated_by: str(apiResponse.updated_by),
    tags,
    availability_rules,
    components,
    channel_overrides,
    modifier_groups,
  };

  const { display: categoryDisplay } = deriveCategoryFromTags(tags);

  return { ...core, category: categoryDisplay };
}

export interface MapMenuV2ToAppOptions {
  /** Current order channel; used for `channel_overrides` price selection. */
  orderChannel: number;
}

/** Maps V2 catalog row to app {@link MenuItemInterface} (modifier_groups only; no legacy addon fields). */
export function mapMenuItemV2ToLegacyShape(
  menuItemV2: MenuItemV2WithCategory,
  options: MapMenuV2ToAppOptions
): MenuItemInterface {
  const fromTags = deriveCategoryFromTags(menuItemV2.tags).display;
  const category = str(menuItemV2.category || fromTags, "").trim();

  const item_price = resolveCompatibilityItemPrice(menuItemV2, options.orderChannel);

  return {
    menu_item_id: str(menuItemV2.menu_item_id),
    restaurant_id: str(menuItemV2.restaurant_id),
    item_name: str(menuItemV2.item_name),
    category,
    description: str(menuItemV2.description),
    image_url: str(menuItemV2.image_url),
    item_price,
    pricing_projection: menuItemV2.pricing_projection,
    promotion: menuItemV2.promotion,
    is_active: bool(menuItemV2.is_available, true),
    is_available: bool(menuItemV2.is_available, true),
    menu_version: num(menuItemV2.menu_version, 0),
    is_deleted: bool(menuItemV2.is_deleted, false),
    count: 0,
    isFavorite: false,
    tags: Array.isArray(menuItemV2.tags) ? menuItemV2.tags : [],
    modifier_groups: Array.isArray(menuItemV2.modifier_groups) ? menuItemV2.modifier_groups : [],
    components: Array.isArray(menuItemV2.components) ? menuItemV2.components : [],
  };
}
